// React hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Helpers i API servisi
import { transformRoute } from "helpers";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import {
  CascoExpiryRow,
  CascoExpirySearchFormValues,
  CascoExpirySummary,
} from "services/api/fleet/casco/types";
import {
  getCascoExpiriesAPI,
  getCascoExpirySummaryAPI,
} from "services/api/fleet/casco/api";

// Hook-ovi
import { useSelectedItemsChanges } from "ui/components/Table/hooks";
import useUrlFilters from "services/hooks/useUrlFilters";

// UI komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { NoResults } from "ui/components/NoResults";
import { FLEET_CASCO_EXPIRY_SEARCH_PAGE_TITLE } from "ui/components/PageTitle/consts";

// Moduli specifični za projekt
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";

// Lokalna navigacija
import { Routes } from "ui/modules/fleet/casco/navigation";

// Lokalne komponente
import {
  CascoExpirySearchPageCmp,
  CascoExpirySearchPageProps,
} from "./types";
import CascoStatusTabs from "./components/CascoStatusTabs/CascoStatusTabs";
import CascoSearchFilters from "./components/CascoSearchFilters/CascoSearchFilters";
import CascoSearchResults from "./components/CascoSearchResults/CascoSearchResults";

const initialFilters: CascoExpirySearchFormValues = {
  search: "",
  expirationDateFrom: null,
  expirationDateTo: null,
  status: "",
};

const initialSummary: CascoExpirySummary = {
  expired: 0,
  expiring: 0,
  valid: 0,
  total: 0,
};

const CascoExpirySearchPage: CascoExpirySearchPageCmp = (
  _props: CascoExpirySearchPageProps
) => {
  const navigate = useNavigate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<CascoExpiryRow>
  >(initialDataWithPagination);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  // Datumski raspon sinkroniziran s URL-om (drill-down s dashboarda + share/reload).
  const [filterValues, setFilterValues] = useUrlFilters<CascoExpirySearchFormValues>(
    initialFilters,
    {
      expirationDateFrom: { param: "expirationDateFrom", type: "date" },
      expirationDateTo: { param: "expirationDateTo", type: "date-end" },
    }
  );
  const [summary, setSummary] = useState<CascoExpirySummary>(initialSummary);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchSearchResult = async (
    pagination: Pagination,
    values: CascoExpirySearchFormValues
  ) => {
    setIsLoading(true);
    try {
      setSearchResults(await getCascoExpiriesAPI(pagination, values));
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const fetchSummary = async () => {
    try {
      setSummary(await getCascoExpirySummaryAPI());
    } catch {
      setSummary(initialSummary);
    }
  };

  useEffect(() => {
    // Kreni s vrijednostima iz URL-a (useUrlFilters ih je već popunio) — ne resetiraj na initial.
    fetchSearchResult(initialDataWithPagination.pagination, filterValues);
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = useCallback(
    (values: CascoExpirySearchFormValues) => {
      setFilterValues(values);
      fetchSearchResult(searchResults.pagination, values);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleStatusChange = useCallback(
    (status: string) => {
      const values = { ...filterValues, status };
      setFilterValues(values);
      fetchSearchResult(searchResults.pagination, values);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterValues, searchResults.pagination]
  );

  const handlePaginationChange = (pagination: Pagination) => {
    fetchSearchResult(pagination, filterValues);
  };

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handleVehicleClick = useCallback(
    (event: React.MouseEvent, vehicleId: string) => {
      event.stopPropagation();
      navigate(
        transformRoute(FleetVehicleRoutes.FleetVehicleDetailPageRouter, {
          id: vehicleId,
        })
      );
    },
    [navigate]
  );

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md border border-b mb-2">
          <PageHeader>
            <PageTitle
              title={FLEET_CASCO_EXPIRY_SEARCH_PAGE_TITLE.text}
              icon={FLEET_CASCO_EXPIRY_SEARCH_PAGE_TITLE.icon}
              iconColor={FLEET_CASCO_EXPIRY_SEARCH_PAGE_TITLE.iconColor}
            />
            <Breadcrumb />
          </PageHeader>
          <CascoStatusTabs
            activeStatus={filterValues.status}
            summary={summary}
            onStatusChange={handleStatusChange}
          />
        </div>
        <div className="px-0 py-6 mx-[-1px] xl:px-6">
          <CascoSearchFilters
            formValues={filterValues}
            onSearchChange={handleSearchChange}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message="Nema vozila" />
          ) : (
            <CascoSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
              onVehicleClick={handleVehicleClick}
            />
          )}
        </div>
      </div>
    </>
  );
};

CascoExpirySearchPage.route = Routes.CascoExpirySearchPageRouter;

export default CascoExpirySearchPage;
